import { RefObject, useState } from 'react';
import { plural } from '@lingui/core/macro';

import { PopoverPlacement } from '@rover/components/src/callouts/Popover/popover.constants';
import { useEnableSchema } from '@rover/components/src/microdata';
import { Rebook, Trophy } from '@rover/icons';
import { Box, Flex, Heading, Text } from '@rover/kibble/core';
import { Badge } from '@rover/kibble/official-patterns/Badge';
import { MaximalCardEventTags } from '@rover/react-lib/src/pages/search/SearchPage/analyticsEvents';
import { getEngagementSignalContent } from '@rover/react-lib/src/pages/search/SearchPage/utilities/engagementSignal';
import {
  highlightNumberInText,
  shouldMaximalCardShowReviewSnippet,
  shouldShowMaximalV2Experience,
  shouldShowRatingsReviews,
  shouldShowRepeatCount,
  shouldShowYearsOfExperience,
} from '@rover/react-lib/src/pages/search/SearchPage/utilities/searchCardTreatment';
import SitterBadgeComponent from '@rover/react-lib/src/pages/search/shared/components/SitterBadge';
import { CommonSearchResult } from '@rover/types/src/CommonSearchResult';
import { PriceTransparencyLevel } from '@rover/types/src/PriceTransparencyLevel';
import { SearchCardTreatment } from '@rover/types/src/SearchCardTreatment';

import { STAR_SITTER } from '../../callouts/CalloutBadge/CalloutBadge.constants';
import SimplifiedRatingsReview from '../../RatingsReview/SimplifiedRatingsReview';
import SitterHighlight from '../../SitterHighlight/SitterHighlight';

import LegacyPriceColumn from './PriceColumn/LegacyPriceColumn';
import MaximalCardGallery from './MaximalCardGallery';
import MaximalCTABox from './MaximalCTABox';
import MaximalReviewSnippet from './MaximalReviewSnippet';
import {
  EXCLUDED_PRIMARY_BADGE_SLUGS,
  SEARCH_RESULT_CARD_Z_INDEX,
} from './SearchResultCard.constants';
import SearchScoreData from './SearchScoreData';
import SkillTagRow from './SkillTagRow';

type Props = {
  index?: number;
  cardTreatment: SearchCardTreatment;
  searchResult: CommonSearchResult;
  sectionRef?: RefObject<HTMLElement | null>;
  preferCatReviews: boolean;
  language: string;
  showFromText: boolean;
  isBHUser: boolean;
  shouldShowPriceInCredits: boolean;
  priceTransparencyLevel?: PriceTransparencyLevel;
  profileUrl: string;
  analyticsProps: MaximalCardEventTags;
  headingId?: string;
  onProfileClick?: () => void;
  isDesktop?: boolean;
  isSeoPage?: boolean;
};

const MaximalCardContent = ({
  index,
  cardTreatment,
  searchResult,
  sectionRef,
  preferCatReviews,
  language,
  showFromText,
  isBHUser,
  shouldShowPriceInCredits,
  priceTransparencyLevel,
  profileUrl,
  analyticsProps,
  headingId,
  onProfileClick,
  isDesktop,
  isSeoPage,
}: Props): JSX.Element => {
  const enableSchema = useEnableSchema();
  const [isBadgeOpen, setIsBadgeOpen] = useState(false);
  const isV2 = shouldShowMaximalV2Experience(cardTreatment);
  // V2 condenses the card: smaller name heading + smaller highlight/ratings/review text & icons.
  const highlightSize: '100' | '200' = isV2 ? '100' : '200';

  const {
    avatarUrl,
    caption,
    currencyCode,
    engagementSignal,
    isStarSitter,
    personalizationTags,
    price,
    priceInCredits,
    primaryBadgeData,
    ratingsAverage,
    repeatClientCount,
    reviewsCount,
    testimonialsCount,
    shortName,
    popoverDebugData,
    unitAsSentence,
    yearsOfExperience,
  } = searchResult;

  const showRatingsReviews = shouldShowRatingsReviews(reviewsCount, testimonialsCount);
  const showRepeatCount = shouldShowRepeatCount(repeatClientCount);
  const showYearsOfExperience = shouldShowYearsOfExperience(
    yearsOfExperience,
    showRatingsReviews,
    showRepeatCount
  );
  const engagementSignalContent = getEngagementSignalContent(engagementSignal);

  const badgeList = [
    isStarSitter ? (
      <Badge
        key="star-sitter"
        {...STAR_SITTER}
        boxProps={{
          ...STAR_SITTER.boxProps,
          webProps: enableSchema ? { itemProp: 'award' } : {},
        }}
      />
    ) : null,
  ].filter(Boolean);

  const sitterName = (
    <Flex flexDirection="row" flexWrap="wrap" gap="2x" mt="2x" alignItems="center">
      <Flex
        webAs="a"
        webAsProps={{
          href: profileUrl,
          target: isDesktop && !isSeoPage ? '_blank' : '_self',
          ...(isSeoPage ? { rel: 'nofollow' } : {}),
          onClick: () => onProfileClick?.(),
        }}
        position="relative"
        zIndex={SEARCH_RESULT_CARD_Z_INDEX.INTERACTIVE.toString()}
        sx={{
          textDecoration: 'none',
          color: 'inherit',
          '&:hover': { textDecoration: 'underline' },
        }}
      >
        <Heading
          mb="0x"
          size={isV2 ? '200' : '300'}
          fontWeight="semibold"
          id={headingId}
          sx={{ wordBreak: 'break-word' }}
          {...(enableSchema ? { itemProp: 'name' } : {})}
        >
          {shortName}
        </Heading>
      </Flex>
      {badgeList.length > 0 && (
        <Flex gap="2x" flexWrap="wrap">
          {badgeList}
        </Flex>
      )}
      {primaryBadgeData && !EXCLUDED_PRIMARY_BADGE_SLUGS.includes(primaryBadgeData.slug) && (
        <Box
          zIndex={
            isBadgeOpen
              ? SEARCH_RESULT_CARD_Z_INDEX.BADGE_OPEN.toString()
              : SEARCH_RESULT_CARD_Z_INDEX.BADGE_CLOSED.toString()
          }
        >
          <SitterBadgeComponent
            title={primaryBadgeData.title}
            content={primaryBadgeData.content}
            imageUrl={primaryBadgeData.imageUrl}
            slug={primaryBadgeData.slug}
            width={24}
            popoverPlacement={PopoverPlacement.RIGHT}
            onChange={setIsBadgeOpen}
            {...(enableSchema ? { itemProp: 'hasCredential' } : {})}
          />
        </Box>
      )}
    </Flex>
  );

  return (
    <Flex ref={sectionRef as any} flexDirection="column" flexGrow="1">
      <Flex flexDirection="column" gap="2x" px="4x" pt="2x">
        <MaximalCardGallery
          avatarUrl={avatarUrl}
          images={searchResult.images}
          imageKey={searchResult.shortName}
          index={index}
          analyticsProps={analyticsProps}
          providerOpk={searchResult.opk}
          providerName={searchResult.shortName}
          profileUrl={profileUrl}
          onProfileClick={onProfileClick}
          isDesktop={isDesktop}
          isSeoPage={isSeoPage}
          isV2={isV2}
        />
        {isV2 ? (
          <Flex flexDirection="row" justifyContent="space-between" alignItems="flex-start" gap="4x">
            <Flex flexDirection="column" gap="2x" flexGrow="1">
              {sitterName}
              <Text mb="2x" size={highlightSize}>
                {caption}
              </Text>
            </Flex>
            <Flex mt="2x" flexShrink="0">
              <LegacyPriceColumn
                stackTotalText
                currencyCode={currencyCode}
                language={language}
                price={price}
                priceInCredits={priceInCredits}
                shouldShowPriceInCredits={shouldShowPriceInCredits}
                showFromText={showFromText}
                unitAsSentence={unitAsSentence}
                priceTransparencyLevel={priceTransparencyLevel}
                isBHUser={isBHUser}
              />
            </Flex>
          </Flex>
        ) : (
          <>
            {sitterName}
            <Text mb="2x" size={highlightSize}>
              {caption}
            </Text>
          </>
        )}
        {popoverDebugData && <SearchScoreData data={popoverDebugData} />}
        {engagementSignalContent && (
          <SitterHighlight
            dataTestId="engagement-signal-highlight"
            text={engagementSignalContent.fullText}
            icon={engagementSignalContent.icon}
            highlightColor={engagementSignalContent.color}
            size={highlightSize}
          />
        )}
        {(showRatingsReviews || showRepeatCount) && (
          <Flex flexDirection="row" flexWrap="wrap" alignItems="center" gap="4x">
            {showRatingsReviews && (
              <SimplifiedRatingsReview
                ratingsAverage={ratingsAverage}
                reviewsCount={reviewsCount}
                testimonialsCount={testimonialsCount}
                size={highlightSize}
              />
            )}
            {showRepeatCount && (
              <SitterHighlight
                dataTestId="repeat-clients-highlight"
                text={highlightNumberInText(
                  plural(repeatClientCount, {
                    one: '# repeat client',
                    other: '# repeat clients',
                  })
                )}
                icon={Rebook}
                size={highlightSize}
              />
            )}
          </Flex>
        )}
        {showYearsOfExperience && (
          <SitterHighlight
            dataTestId="years-of-experience-highlight"
            text={highlightNumberInText(yearsOfExperience)}
            icon={Trophy}
            size={highlightSize}
          />
        )}
        {shouldMaximalCardShowReviewSnippet(cardTreatment) && (
          <MaximalReviewSnippet
            searchResult={searchResult}
            preferCatReviews={preferCatReviews}
            analyticsProps={analyticsProps}
            isV2={isV2}
          />
        )}
        {!isV2 && <SkillTagRow tags={personalizationTags} />}
      </Flex>
      <MaximalCTABox
        searchResult={searchResult}
        language={language}
        showFromText={showFromText}
        isBHUser={isBHUser}
        shouldShowPriceInCredits={shouldShowPriceInCredits}
        priceTransparencyLevel={priceTransparencyLevel}
        profileUrl={profileUrl}
        analyticsProps={analyticsProps}
        isDesktop={isDesktop}
        isSeoPage={isSeoPage}
        isV2={isV2}
      />
    </Flex>
  );
};

export default MaximalCardContent;
